import { computed, ref } from 'vue'
const confirmModalData = ref(null)
const isOpen = computed(() => !!confirmModalData.value)

export function useConfirm() {
  const showConfirm = ({
    title = 'Unsaved Changes',
    message = 'You have unsaved changes. Are you sure you want to leave?',
    icon = 'warning',
    confirmText = 'Continue',
    cancelText = 'Cancel',
    confirmColor = 'cta',
    cancelColor = 'secondary',
    requiresTypeConfirmation = false,
    typeConfirmationText = '',
    confirmInput,
    onConfirm,
    onCancel,
  } = {}) => {
    confirmModalData.value = {
      title,
      message,
      icon,
      confirmText,
      cancelText,
      confirmColor,
      cancelColor,
      requiresTypeConfirmation,
      typeConfirmationText,
      confirmInput,
      onConfirm,
      onCancel,
    }
  }

  const closeConfirm = () => {
    confirmModalData.value = null
  }

  // Common confirmation modal presets
  const confirmDelete = (itemName = 'item', onConfirm) => {
    showConfirm({
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete this ${itemName}?`,
      icon: 'delete',
      confirmText: 'Delete',
      confirmColor: 'warning',
      onConfirm,
    })
  }

  const confirmDiscard = (onConfirm) => {
    showConfirm({
      title: 'Discard Changes',
      message: 'Are you sure you want to discard all changes?',
      icon: 'delete_sweep',
      confirmText: 'Discard',
      onConfirm,
    })
  }

  const confirmLeave = (onConfirm, onCancel) => {
    showConfirm({
      title: 'Unsaved Changes',
      message: 'You have unsaved changes. Are you sure you want to leave this page?',
      icon: 'warning',
      confirmText: 'Continue',
      onConfirm,
      onCancel,
    })
  }

  const confirmInactivate = (itemName = 'item', onConfirm) => {
    showConfirm({
      title: 'Inactivate Company',
      message: `Are you sure you want to inactivate this ${itemName}?`,
      icon: 'archive',
      confirmText: 'Inactivate',
      onConfirm,
    })
  }

  const confirmReset = (itemName = 'form', onConfirm) => {
    showConfirm({
      title: 'Reset',
      message: `Are you sure you want to reset this ${itemName} to its default state?`,
      icon: 'restart_alt',
      confirmText: 'Reset',
      onConfirm,
    })
  }

  const confirmClearCache = (onConfirm) => {
    showConfirm({
      title: 'Clear cache',
      message:
        'Cached company and request lists will be refetched. You stay signed in. Unsaved edits on this page will be lost.',
      icon: 'cached',
      confirmText: 'Clear cache',
      onConfirm,
    })
  }

  const confirmCountryChange = (onConfirm) => {
    showConfirm({
      title: 'Confirm Address Change',
      message: 'Are you sure you want to change the address?',
      icon: 'location_on',
      confirmText: 'Change',
      onConfirm,
    })
  }

  const confirmAiAssessment = (itemName = 'company', onConfirm) => {
    showConfirm({
      title: 'Create AI Assessment',
      message: `This action will trigger the creation of an AI Assessment on this ${itemName}.`,
      icon: 'check_circle',
      confirmText: 'Create',
      onConfirm,
    })
  }

  const confirmDeleteCompany = (companyName = 'company', onConfirm) => {
    showConfirm({
      title: 'Delete Company',
      message: `This action will permanently delete "${companyName}" and all associated data. This cannot be undone.`,
      icon: 'delete_forever',
      confirmText: 'Delete Company',
      confirmColor: 'warning',
      requiresTypeConfirmation: true,
      typeConfirmationText: companyName,
      onConfirm,
    })
  }

  const confirmDeleteProduct = (productName = 'product', onConfirm) => {
    showConfirm({
      title: 'Delete Product',
      message: `This action will permanently delete "${productName}" and all associated data. This cannot be undone.`,
      icon: 'delete_forever',
      confirmText: 'Delete Product',
      confirmColor: 'warning',
      requiresTypeConfirmation: true,
      typeConfirmationText: productName,
      onConfirm,
    })
  }

  const confirmSetMain = (itemName = 'item', onConfirm) => {
    showConfirm({
      title: 'Set as Main',
      message: `Are you sure you want to set this ${itemName} as the main one?`,
      icon: 'star',
      confirmText: 'Set as Main',
      cancelText: 'Cancel',
      confirmColor: 'cta',
      onConfirm,
    })
  }

  const confirmCloseGeneratingDoc = (onConfirm) => {
    showConfirm({
      title: 'Close Document Generation',
      message:
        'By closing, document generation will continue in the background and you will not be provided with the direct link. Once complete, you can find it in the Google Drive folder.',
      icon: 'warning',
      confirmText: 'Close anyway',
      cancelText: 'Keep waiting',
      confirmColor: 'warning',
      onConfirm,
    })
  }

  const confirmRunAiAssessment = (itemName = 'company', onConfirm) => {
    showConfirm({
      title: 'Run AI Assessment?',
      message: `Would you like to run an AI assessment for this ${itemName} now?`,
      icon: 'psychology',
      confirmText: 'Run Assessment',
      cancelText: 'Skip for now',
      confirmColor: 'cta',
      onConfirm,
    })
  }

  return {
    isOpen,
    confirmModalData,
    showConfirm,
    closeConfirm,
    // Preset helpers
    confirmDelete,
    confirmDiscard,
    confirmLeave,
    confirmInactivate,
    confirmReset,
    confirmClearCache,
    confirmCountryChange,
    confirmAiAssessment,
    confirmDeleteCompany,
    confirmDeleteProduct,
    confirmSetMain,
    confirmCloseGeneratingDoc,
    confirmRunAiAssessment,
  }
}
